import React, { Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReactGA from "react-ga4";
import { initTagManager } from "helpers/tagManagerHelper";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import { useVersionCheck } from "hooks/useVersionCheck";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ROUTES, { RenderRoutes } from "./routes";
import PreLoadPagesSpin from "components/PreLoadPagesSpin";
import * as Sentry from "@sentry/react";
// Ag Grid Style
import "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "ag-grid-community/dist/styles/ag-theme-balham-dark.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "./styles/styles.scss";

const GlobalState = lazyWithRetry(() => import("contexts/GlobalContext"));
const queryClient = new QueryClient({});

const App = () => {
  useVersionCheck();

  useEffect(() => {
    if (process.env.REACT_APP_NODE_ENV === "development") {
      const originalError = console.error;
      console.error = (...args) => {
        if (
          args.some(
            (arg) =>
              typeof arg === "string" &&
              (arg.startsWith("*") || arg.startsWith("AG Grid:"))
          )
        ) {
          return;
        }
        originalError(...args);
      };
    }
  }, []);

  useEffect(() => {
    let timeoutId, intervalId;

    const scheduleMidnightReload = () => {
      const now = new Date();
      const nextMidnight = new Date();

      // 🕛 Set target to next midnight
      nextMidnight.setHours(24, 0, 0, 0);

      // If it’s already past midnight (rare edge case), push one day ahead
      if (nextMidnight <= now) {
        nextMidnight.setDate(nextMidnight.getDate() + 1);
      }

      const delay = nextMidnight - now;

      timeoutId = setTimeout(() => {
        window.location.reload(true); // 🔁 refresh at midnight

        // Then keep doing it every 24 hours
        intervalId = setInterval(() => {
          window.location.reload(true);
        }, 24 * 60 * 60 * 1000);
      }, delay);
    };

    scheduleMidnightReload();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, []);

  // Initialize Google Analytics
  if (process.env.REACT_APP_GA_TRACKING_CODE) {
    ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_CODE);
    if (process.env.REACT_APP_NODE_ENV) {
      ReactGA.set({ app_environment: process.env.REACT_APP_NODE_ENV });
    }
  }

  // Initialize Google Tag Manager
  if (process.env.REACT_APP_TAG_MANAGER_ID) {
    initTagManager(process.env.REACT_APP_TAG_MANAGER_ID);
  }

  return (
    <Suspense fallback={<PreLoadPagesSpin />}>
      <QueryClientProvider client={queryClient}>
        <GlobalState>
          <main id="content-root">
            <RenderRoutes routes={ROUTES} />
          </main>
        </GlobalState>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Suspense>
  );
};

export default Sentry.withProfiler(App);
